"use client";

import type { YMapLocationRequest } from "@yandex/ymaps3-types";
import clsx from "clsx";
import customizationMap from "public/customization.json";
import { useEffect, useRef } from "react";

import type { MapPoints } from "@shared/types/map-types";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { LoadingSpinner } from "@shared/ui/loading-spinner";
import { LogoLetter } from "@shared/ui/logo";

import { useYMaps } from "../../providers/yandex-maps-context";
import { Point } from "./point";

import s from "./location-map.module.scss";

interface LocationMapProps {
  coordinates?: [number, number] | null;
  zoom?: number;
  className?: string;

  mapPoints?: MapPoints;
  openPointId?: string | null;
  /** ID of the primary object that should always be open (e.g., current cultural object page) */
  primaryObjectId?: string | null;
  /** If true, resort point will always be open */
  alwaysOpenResort?: boolean;
  /** Enable scroll zoom behavior */
  enableScrollZoom?: boolean;
  /** Initial bounds for the map - alternative to center+zoom */
  bounds?: [[number, number], [number, number]];

  onMarkersLayout?: (layout: {
    containerWidth: number;
    maxMarkerWidth: number;
  }) => void;
}

export const LocationMap = ({
  coordinates,
  zoom = 18,
  className,
  mapPoints,
  openPointId = null,
  primaryObjectId = null,
  alwaysOpenResort = false,
  enableScrollZoom = false,
  bounds,
  onMarkersLayout,
}: LocationMapProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const lastLayoutRef = useRef<string | null>(null);

  useEffect(() => {
    // Validate coordinates
  }, [coordinates, zoom, bounds, mapPoints]);

  const {
    isLoaded,
    error,
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapListener,
    YMapControls,
    ZoomControl,
    GeolocationControl,
    reactify,
    location,
    setCenter,
  } = useYMaps();

  useEffect(() => {
    if (setCenter && (coordinates || bounds)) {
      // Используем bounds если они есть, иначе center+zoom
      const locationUpdate: YMapLocationRequest = bounds
        ? { bounds, duration: 600 }
        : {
            center: coordinates as [number, number],
            zoom,
            duration: 600,
          };

      setCenter(locationUpdate);
    }
  }, [coordinates, zoom, bounds, setCenter]);

  useEffect(() => {
    // YMaps readiness check
  }, [
    isLoaded,
    error,
    YMap,
    YMapMarker,
    YMapListener,
    YMapControls,
    ZoomControl,
    GeolocationControl,
  ]);

  useEffect(() => {
    if (!isLoaded || !mapPoints) return;

    let retryCount = 0;
    const maxRetries = 10;

    // Wait for markers to be rendered in DOM
    const addYItemClass = () => {
      const points = rootRef?.current?.querySelectorAll(".ymaps3x0--marker");

      if (points && points.length > 0) {
        // Measure marker widths for right-side padding logic
        try {
          const containerWidth = rootRef.current?.clientWidth ?? 0;
          let maxMarkerWidth = 0;
          points.forEach((el) => {
            if (el instanceof Element) {
              const w = el.getBoundingClientRect().width;
              if (Number.isFinite(w) && w > maxMarkerWidth) maxMarkerWidth = w;
            }
          });

          if (containerWidth > 0 && maxMarkerWidth > 0 && onMarkersLayout) {
            const key = `${containerWidth}:${Math.round(maxMarkerWidth)}`;
            if (lastLayoutRef.current !== key) {
              console.log("key", containerWidth, maxMarkerWidth);
              lastLayoutRef.current = key;
              onMarkersLayout({
                containerWidth,
                maxMarkerWidth,
              });
            }
          }
        } catch {
          // Error handling for marker layout measurement
        }

        points.forEach((point) => {
          if (point instanceof Element) {
            point.classList.add(s.yItem);
          }
        });
      } else if (retryCount < maxRetries) {
        // Retry if markers aren't ready yet
        retryCount++;
        setTimeout(addYItemClass, 50);
      } else {
        // Failed after retries
      }
    };

    // Initial delay to ensure React has rendered the Points
    const timeoutId = setTimeout(addYItemClass, 100);

    return () => clearTimeout(timeoutId);
  }, [isLoaded, mapPoints]);

  useEffect(() => {
    if (!isLoaded || mapPoints || !coordinates) return;

    let retryCount = 0;
    const maxRetries = 10;

    // Wait for single marker to be rendered in DOM
    const addYItemClassToSingleMarker = () => {
      const points = rootRef?.current?.querySelectorAll(".ymaps3x0--marker");

      if (points && points.length > 0) {
        points.forEach((point) => {
          if (point instanceof Element) {
            point.classList.add(s.singleMarker);
          }
        });
      } else if (retryCount < maxRetries) {
        // Retry if marker isn't ready yet
        retryCount++;
        setTimeout(addYItemClassToSingleMarker, 50);
      }
    };

    // Initial delay to ensure React has rendered the marker
    const timeoutId = setTimeout(addYItemClassToSingleMarker, 100);

    return () => clearTimeout(timeoutId);
  }, [isLoaded, mapPoints, coordinates]);

  if (error) {
    return (
      <div className={clsx(s.root, s.status, className)}>
        <div className={s.error}>Ошибка загрузки карты: {error}</div>
      </div>
    );
  }

  if (
    !isLoaded ||
    !YMap ||
    !YMapDefaultSchemeLayer ||
    !YMapDefaultFeaturesLayer ||
    !YMapListener ||
    !YMapControls ||
    !ZoomControl ||
    !GeolocationControl ||
    !YMapMarker
  ) {
    return (
      <div className={clsx(s.root, s.status, className)}>
        <LoadingSpinner />
        <div className={s.loading}>Загрузка карты...</div>
      </div>
    );
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
  const behaviors = reactify.useDefault(
    [
      "drag",
      enableScrollZoom ? "scrollZoom" : null,
      "dblClick",
      "mouseRotate",
      "mouseTilt",
      "pinchZoom",
    ].filter(Boolean),
  );

  return (
    <div className={clsx(s.root, className)} ref={rootRef}>
      <YMap location={location} behaviors={behaviors}>
        <YMapDefaultSchemeLayer customization={customizationMap} />
        <YMapDefaultFeaturesLayer />
        <YMapListener
          onUpdate={({
            location: newLocation,
          }: {
            location: {
              center: [number, number];
              zoom: number;
              duration?: number;
            };
          }) => {
            if (bounds) return;
            setCenter(newLocation);
          }}
        />
        <YMapControls position="right">
          <ZoomControl />
        </YMapControls>

        {mapPoints?.webcams?.map((cam) => (
          <Point
            key={cam.documentId}
            coordinates={cam.coordinates}
            title={cam.title}
            type="Вебкамеры"
            icon="webcamera"
            onClick={cam.onClick}
            href={cam.href}
            target={cam.target}
            isOpen={
              openPointId === cam.documentId ||
              primaryObjectId === cam.documentId
            }
          />
        ))}

        {mapPoints?.["cafes-restaurants"]?.map((point, idx) => (
          <Point
            key={point.documentId}
            coordinates={point.coordinates}
            title={point.title}
            type="Рестораны и кафе"
            icon="food"
            onClick={point.onClick}
            href={point.href}
            target={point.target}
            isOpen={
              openPointId === point.documentId ||
              primaryObjectId === point.documentId
            }
          />
        ))}

        {mapPoints?.accommodations?.map((point, idx) => (
          <Point
            key={point.documentId}
            coordinates={point.coordinates}
            title={point.title}
            type="Жилье"
            icon="bed"
            onClick={point.onClick}
            href={point.href}
            target={point.target}
            isOpen={
              openPointId === point.documentId ||
              primaryObjectId === point.documentId
            }
          />
        ))}

        {mapPoints?.["cultural-objects"]?.map((culturalObject) => (
          <Point
            key={culturalObject.documentId}
            coordinates={culturalObject.coordinates}
            title={culturalObject.title}
            type="Культурный объект"
            icon="skulp"
            onClick={culturalObject.onClick}
            href={culturalObject.href}
            target={culturalObject.target}
            isOpen={
              openPointId === culturalObject.documentId ||
              primaryObjectId === culturalObject.documentId
            }
          />
        ))}

        {mapPoints?.resort && (
          <Point
            key={mapPoints.resort.documentId}
            coordinates={mapPoints.resort.coordinates}
            title={mapPoints.resort.title}
            type="Горнолыжный курорт"
            customIcon={<LogoLetter className={s.resortLogo} color="milk" />}
            onClick={mapPoints.resort.onClick}
            href={mapPoints.resort.href}
            target={mapPoints.resort.target}
            isOpen={
              alwaysOpenResort ||
              openPointId === mapPoints.resort.documentId ||
              primaryObjectId === mapPoints.resort.documentId
            }
          />
        )}

        {!mapPoints && coordinates && (
          <YMapMarker coordinates={coordinates}>
            <Button>
              <BrandIcon
                name="location"
                fillColor="cadmium"
                strokeColor="white"
                className={s.icon}
              />
            </Button>
          </YMapMarker>
        )}
      </YMap>
    </div>
  );
};
